/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState } from 'react';
import { BarWave } from 'react-cssfx-loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faSearch,
  faEdit,
  faTrash,
  faEye,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { Modal } from 'flowbite-react';
import { useGetUsersQuery, useDeleteUserMutation } from '../../../redux/apiSlice';
import usePagination from '../../../components/Pagination/PaginationHook';
import Pagination from '../../../components/Pagination/Pagination';
import UserModal from '../../components/UserModal';

function Users() {
  const [searchTerm, setSearchTerm] = useState({ searchTerm: '' });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [deleteMessage, setDeleteMessage] = useState('');
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [userIdToDelete, setUserIdToDelete] = useState(null);

  const {
    data: users,
    error: usersError,
    isLoading: usersLoadingStatus,
    isSuccess: usersSuccessStatus,
    isError: usersErrorStatus,
    refetch,
  } = useGetUsersQuery('', { refetchOnMountOrArgChange: true });

  // Safely access users array with fallback, and filter locally to avoid input remount/focus loss
  const rawUsers = users?.users || [];
  const keyword = (searchTerm.searchTerm || '').toLowerCase();
  const usersArray = rawUsers.filter((u) => (
    (u.name || '').toLowerCase().includes(keyword)
    || (u.email || '').toLowerCase().includes(keyword)
    || (u.department || '').toLowerCase().includes(keyword)
    || (u.position || '').toLowerCase().includes(keyword)
  ));
  const [deleteUser] = useDeleteUserMutation();

  const requestDeleteUser = (userId) => {
    setUserIdToDelete(userId);
    setOpenDeleteModal(true);
  };

  const confirmDeleteUser = async () => {
    if (!userIdToDelete) return;
    try {
      await deleteUser(userIdToDelete).unwrap();
      setDeleteMessage('User deleted successfully.');
      setTimeout(() => setDeleteMessage(''), 2000);
      setOpenDeleteModal(false);
      setUserIdToDelete(null);
      // Force refresh of the list
      try { await refetch(); } catch (e) { /* ignore */ }
    } catch (e) {
      setDeleteMessage('Failed to delete user.');
      setTimeout(() => setDeleteMessage(''), 2000);
      setOpenDeleteModal(false);
      setUserIdToDelete(null);
    }
  };

  const {
    handleNext,
    handlePrevious, itemsDisplaying: usersDisplaying,
  } = usePagination(usersArray, 15);

  const handleSearch = (e) => {
    setSearchTerm({
      ...searchTerm,
      [e.target.name]: e.target.value,
    });
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setModalMode('create');
    setShowUserModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setModalMode('edit');
    setShowUserModal(true);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setModalMode('view');
    setShowUserModal(true);
  };

  const handleCloseModal = async (didSave = false) => {
    setShowUserModal(false);
    setSelectedUser(null);
    if (didSave) {
      try { await refetch(); } catch (e) { /* ignore */ }
    }
  };

  let content;

  if (usersLoadingStatus && usersArray.length === 0) {
    content = (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <BarWave color="#6366f1" height="2rem" width="8rem" />
          <p className="mt-4 text-gray-300">Loading users...</p>
        </div>
      </div>
    );
  } else if (usersErrorStatus) {
    content = (
      <div className="bg-red-900/20 border border-red-700 text-red-300 px-6 py-4 rounded-lg">
        <div className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faTrash} className="text-red-400" />
          <div>
            <h3 className="font-semibold">Error loading users</h3>
            <p className="text-sm text-red-200">
              {usersError?.data?.message || usersError?.message || 'Unknown error occurred'}
            </p>
          </div>
        </div>
      </div>
    );
  } else if (usersSuccessStatus && usersArray.length > 0) {
    content = (
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700">
        {/* Header Section */}
        <div className="bg-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-white">User Management</h1>
              <p className="text-gray-300 text-sm">Manage system users and permissions</p>
            </div>
            <button
              type="button"
              onClick={handleCreateUser}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FontAwesomeIcon icon={faPlus} className="text-sm" />
              <span className="font-medium">Create User</span>
            </button>
          </div>
        </div>

        {deleteMessage && (
          <div className="mx-6 mt-4 bg-green-900/20 border border-green-700 text-green-300 px-4 py-3 rounded-lg">
            <p className="text-center">{deleteMessage}</p>
          </div>
        )}

        {/* Search Section */}
        <div className="px-6 py-4 bg-gray-750 border-b border-gray-600">
          <div className="flex items-center justify-between">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  name="searchTerm"
                  placeholder="Search users..."
                  value={searchTerm.searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white placeholder-gray-400"
                />
              </div>
            </div>
            <div className="ml-4 text-sm text-gray-300">
              {usersArray.length}
              {' '}
              user
              {usersArray.length !== 1 ? 's' : ''}
              {' '}
              found
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-750">
              <tr className="border-b border-gray-600">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Position
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {usersArray.slice(usersDisplaying - 15, usersDisplaying).map((user, index) => (
                <tr key={user.id} className={`hover:bg-gray-750 ${index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {user.name?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-white">{user.name}</div>
                        <div className="text-sm text-gray-400">
                          @
                          {user.username}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-white">{user.position}</div>
                    <div className="text-sm text-gray-400">{user.category}</div>
                  </td>
                  <td className="px-4 py-4">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-900 text-blue-200">
                      {user.department}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-white">{user.email}</div>
                    <div className="text-sm text-gray-400">{user.phone_number}</div>
                  </td>
                  <td className="px-4 py-4">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-900 text-green-200">
                      {user.role}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-center space-x-2">
                      <button
                        type="button"
                        onClick={() => handleViewUser(user)}
                        className="p-1 text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 rounded"
                        title="View User"
                      >
                        <FontAwesomeIcon icon={faEye} className="text-sm" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleEditUser(user)}
                        className="p-1 text-green-400 hover:text-green-300 hover:bg-green-900/20 rounded"
                        title="Edit User"
                      >
                        <FontAwesomeIcon icon={faEdit} className="text-sm" />
                      </button>
                      <button
                        type="button"
                        className="p-1 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded"
                        title="Delete User"
                        onClick={() => requestDeleteUser(user.id)}
                      >
                        <FontAwesomeIcon icon={faTrash} className="text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 bg-gray-750 border-t border-gray-600">
          <Pagination
            itemsDisplaying={usersDisplaying}
            itemsPerPage={15}
            items={usersArray}
            handleNext={handleNext}
            handlePrevious={handlePrevious}
          />
        </div>
      </div>
    );
  } else if (usersSuccessStatus && usersArray.length === 0) {
    content = (
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-8">
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <FontAwesomeIcon icon={faUsers} className="text-2xl text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-white mb-2">No users found</h3>
          <p className="text-gray-400 mb-6">Get started by creating your first user</p>
          <button
            type="button"
            onClick={handleCreateUser}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Create First User
          </button>
        </div>
      </div>
    );
  } else {
    content = (
      <div className="bg-yellow-900/20 border border-yellow-700 text-yellow-300 px-6 py-4 rounded-lg">
        <div className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faSearch} className="text-yellow-400" />
          <div>
            <h3 className="font-semibold">Loading users...</h3>
            <p className="text-sm text-yellow-200">Please wait while we fetch the data</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 w-full min-h-screen bg-gray-900">
      {content}

      {/* User Modal */}
      <UserModal
        isOpen={showUserModal}
        onClose={handleCloseModal}
        userId={selectedUser?.id}
        mode={modalMode}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={openDeleteModal} size="md" onClose={() => setOpenDeleteModal(false)} popup>
        <Modal.Header className="bg-gray-700 border-b border-gray-600">
          <h3 className="text-lg font-semibold text-white">Delete User</h3>
        </Modal.Header>
        <Modal.Body className="bg-gray-800">
          <div className="text-center p-6">
            <div className="w-16 h-16 bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <FontAwesomeIcon icon={faTrash} className="text-2xl text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Confirm Deletion</h3>
            <p className="text-gray-300 mb-6">Are you sure you want to delete this user? This action cannot be undone.</p>
            <div className="flex items-center justify-center space-x-4">
              <button
                type="button"
                onClick={() => setOpenDeleteModal(false)}
                className="px-4 py-2 text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteUser}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete User
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default Users;
